import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';

import { ApiError } from '../../shared/api/client.ts';
import type { EnvioDeEmail, OrigemDeEnvio } from '../../shared/api/tipos.ts';
import { formatarDataHora, formatarDataSimples } from '../../shared/format.ts';

/**
 * A tabela do log de e-mails, com os estados de carga.
 *
 * Mora fora das telas porque duas delas a mostram — o log em
 * `/envios-de-email` e a aba de histórico de Pendências —, e duas cópias
 * divergiriam na primeira coluna que alguém acrescentasse. Quem é dono do
 * filtro e da paginação é cada tela; aqui chegam as linhas já resolvidas.
 *
 * `aoRevogar` ausente esconde o botão: ele existe só para administrador, e é
 * a rota da API que recusa de verdade.
 */
export function TabelaDeEnvios({
  itens,
  carregando,
  recarregando = false,
  erro,
  vazio,
  aoRevogar,
}: {
  itens: readonly EnvioDeEmail[];
  carregando: boolean;
  recarregando?: boolean;
  erro?: unknown;
  /** O que dizer quando não há linha nenhuma — depende do filtro de cada tela. */
  vazio: string;
  aoRevogar?: ((cliente: { empresaId: string; razaoSocial: string }) => void) | undefined;
}) {
  if (erro !== undefined && erro !== null) {
    return (
      <Alert severity="error">
        Não foi possível carregar o log de envios. {mensagemDoErro(erro)}
      </Alert>
    );
  }

  if (carregando) {
    return (
      <Stack spacing={1}>
        {Array.from({ length: 5 }, (_, indice) => (
          <Skeleton key={indice} variant="rounded" height={40} />
        ))}
      </Stack>
    );
  }

  if (itens.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
        {vazio}
      </Typography>
    );
  }

  return (
    <TableContainer sx={{ opacity: recarregando ? 0.6 : 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Quando</TableCell>
            <TableCell>Cliente</TableCell>
            <TableCell>Período</TableCell>
            <TableCell align="right">Notas</TableCell>
            <TableCell>Destinatários</TableCell>
            <TableCell>Origem</TableCell>
            <TableCell>Situação</TableCell>
            <TableCell>Link</TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {itens.map((envio) => (
            <LinhaDeEnvio
              key={envio.id}
              envio={envio}
              aoRevogar={
                envio.empresaId === null || aoRevogar === undefined
                  ? undefined
                  : () =>
                      aoRevogar({
                        empresaId: envio.empresaId ?? '',
                        razaoSocial: envio.razaoSocial ?? 'este cliente',
                      })
              }
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function LinhaDeEnvio({
  envio,
  aoRevogar,
}: {
  envio: EnvioDeEmail;
  /** Ausente para envio sem cliente: não há links daquele cadastro para fechar. */
  aoRevogar?: (() => void) | undefined;
}) {
  return (
    <TableRow hover>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatarDataHora(envio.enviadoEm)}</TableCell>

      <TableCell>
        <Typography variant="body2">{envio.razaoSocial ?? '—'}</Typography>
        {envio.cnpjFormatado !== null && (
          <Typography variant="caption" color="text.secondary">
            {envio.cnpjFormatado}
          </Typography>
        )}
        {/* O assunto é a frase que o contador viu na caixa dele, e é por ela
            que o escritório confere que a mensagem certa saiu. */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {envio.assunto}
        </Typography>
      </TableCell>

      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        {envio.rotuloDoPeriodo ?? rotuloDeDatas(envio)}
      </TableCell>

      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {envio.quantidadeDeNotas ?? '—'}
      </TableCell>

      <TableCell>
        {envio.destinatarios.length === 0 ? (
          '—'
        ) : (
          <Stack spacing={0.25}>
            {envio.destinatarios.map((email) => (
              <Typography key={email} variant="caption">
                {email}
              </Typography>
            ))}
          </Stack>
        )}
      </TableCell>

      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <Typography variant="caption" color="text.secondary">
          {rotuloDaOrigem(envio.origem)}
        </Typography>
      </TableCell>

      <TableCell>
        <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
          <Chip
            size="small"
            label={envio.situacao === 'enviado' ? 'enviado' : 'falhou'}
            color={envio.situacao === 'enviado' ? 'success' : 'error'}
          />
          {envio.erro !== null && (
            <Tooltip title={envio.erro}>
              <Typography
                variant="caption"
                color="error"
                sx={{
                  maxWidth: 200,
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {envio.erro}
              </Typography>
            </Tooltip>
          )}
        </Stack>
      </TableCell>

      <TableCell>
        <EstadoDoLink envio={envio} />
      </TableCell>

      <TableCell align="right">
        {aoRevogar !== undefined && envio.acessoPublicoId !== null && (
          <Tooltip title="Revogar todos os links deste cliente">
            <IconButton size="small" onClick={aoRevogar} aria-label="Revogar links">
              <LinkOffOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
}

/**
 * O que aconteceu com o link depois do envio.
 *
 * "O contador abriu?" é a pergunta do dia 10, e a hora do último acesso fica à
 * vista: um "sim" sozinho não diz se ele abriu no dia do envio ou agora.
 */
function EstadoDoLink({ envio }: { envio: EnvioDeEmail }) {
  if (envio.acessoPublicoId === null) {
    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Stack spacing={0.25}>
      <Chip
        size="small"
        variant="outlined"
        color={envio.linkAtivo ? 'success' : 'default'}
        label={envio.linkAtivo ? 'ativo' : 'encerrado'}
      />
      {envio.linkExpiraEm !== null && (
        <Typography variant="caption" color="text.secondary">
          vale até {formatarDataHora(envio.linkExpiraEm)}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary">
        {envio.linkAcessos === 0
          ? 'nunca aberto'
          : `${envio.linkAcessos} acesso(s), último em ${formatarDataHora(
              envio.linkUltimoAcessoEm ?? '',
            )}`}
      </Typography>
    </Stack>
  );
}

function rotuloDeDatas(envio: EnvioDeEmail): string {
  if (envio.periodoDe === null || envio.periodoAte === null) return '—';
  return `${formatarDataSimples(envio.periodoDe)} a ${formatarDataSimples(envio.periodoAte)}`;
}

function rotuloDaOrigem(origem: OrigemDeEnvio): string {
  if (origem === 'fechamento-automatico') return 'fechamento (dia 5)';
  if (origem === 'fechamento-painel') return 'fechamento (painel)';
  if (origem === 'arquivos-fiscais') return 'arquivos fiscais';
  return 'outro';
}

function mensagemDoErro(erro: unknown): string {
  if (erro instanceof ApiError) return erro.message;
  return erro instanceof Error ? erro.message : 'Falha inesperada.';
}
